<template>
  <div class="form-step">
    <div class="section-header d-flex justify-content-between align-items-center">
      <div class="col-md-6">
        <h5 class="section-title">personal details</h5>
      </div>
      <div class="col-md-6" style="text-align: right;">
        <button
          type="button"
          class="btn btn-outline-info me-2"
          @click="openPMCModal"
        >
          Europe PMC Search
        </button>
        <button
        type="button"
        class="btn btn-outline-primary"
        @click="showOrcidInput = !showOrcidInput"
      >
        {{ showOrcidInput ? 'Cancel' : (personal.orcidId ? 'Edit ORCID ID' : 'Add ORCID ID') }}
      </button>
      </div>
    </div>

    <div v-if="showOrcidInput" class="row mt-3">
      <div class="col-md-6">
        <label class="form-label">ORCID ID</label>
        <input
          type="text"
          class="form-control"
          v-model="personal.orcidId"
          placeholder="0000-0002-1825-0097"
          @input="emitUpdate"
        >
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <label class="form-label">Title</label>
        <select
          class="form-select"
          v-model="personal.title"
          @change="emitUpdate"
        >
          <option value="">Select Title</option>
          <option value="Mr">Mr</option>
          <option value="Mrs">Mrs</option>
          <option value="Ms">Ms</option>
          <option value="Dr">Dr</option>
          <option value="Associate Professor">Associate Professor</option>
          <option value="Professor">Professor</option>
        </select>
      </div>
      <div class="col-md-6">
        <label class="form-label">Country of Origin</label>
        <select
          class="form-select"
          v-model="personal.country"
          @change="emitUpdate"
        >
          <option value="">Select Country</option>
          <option v-for="country in africanCountries" :key="country.value" :value="country.value">
            {{ country.label }}
          </option>
        </select>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-6">
        <label class="form-label">Current organisation</label>
        <input
          type="text"
          class="form-control"
          v-model="personal.currentOrganisation"
          @input="emitUpdate"
          placeholder="Nuvoteq"
        >
      </div>
      <div class="col-md-6">
        <label class="form-label">Current job title</label>
        <input
          type="text"
          class="form-control"
          v-model="personal.currentJobTitle"
          @input="emitUpdate"
          placeholder="Solutions Architect"
        >
      </div>
    </div>
    <br>
    <div class="row mb-3">
      <span style="color: #009CAD !important">
        Please select one or two working groups that best align with your expertise and professional interests
      </span>
      <div class="col-md-6">
        <label class="form-label">Select Working Group 1</label>
        <div class="specialisation-tags mb-2" v-if="personal.selectedWorkingGroup1">
          <span class="badge bg-info me-2 mb-1">
            {{ getWorkingGroupName(personal.selectedWorkingGroup1) }}
            <i class="bi bi-x ms-1" @click="removeWorkingGroup1" style="cursor: pointer;"></i>
          </span>
        </div>
        <select
          class="form-select"
          v-model="personal.selectedWorkingGroup1"
          @change="handleWorkingGroupSelection($event.target.value, 1)"
        >
          <option value="">Select Working Group</option>
          <option
            v-for="group in workingGroups"
            :key="group.id"
            :value="group.id"
            :disabled="isPendingApproval(group.id)"
          >
            {{ getWorkingGroupOptionLabel(group) }}
          </option>
        </select>
      </div>
      <div class="col-md-6">
        <label class="form-label">Select Working Group 2</label>
        <div class="working-group-tags mb-2" v-if="personal.selectedWorkingGroup2">
          <span class="badge bg-secondary me-2 mb-1">
            {{ getWorkingGroupName(personal.selectedWorkingGroup2) }}
            <i class="bi bi-x ms-1" @click="removeWorkingGroup2" style="cursor: pointer;"></i>
          </span>
        </div>
        <select
          class="form-select"
          v-model="personal.selectedWorkingGroup2"
          @change="handleWorkingGroupSelection($event.target.value, 2)"
        >
          <option value="">Select Working Group</option>
          <option
            v-for="group in workingGroups"
            :key="group.id"
            :value="group.id"
            :disabled="isPendingApproval(group.id)"
          >
            {{ getWorkingGroupOptionLabel(group) }}
          </option>
        </select>
      </div>
    </div>
    <div class="mb-3">
      <label class="form-label">Biography</label>
      <textarea
        class="form-control"
        rows="4"
        v-model="personal.biography"
        @input="emitUpdate"
        placeholder="Tell us about yourself..."
      ></textarea>
    </div>
    <div class="row mb-3 align-items-start g-3">
      <div class="col-md-4 d-flex flex-column">
        <label class="form-label">
          <i class="bi bi-linkedin me-2"></i>LinkedIn Link
        </label>
        <input
          type="text"
          class="form-control"
          v-model="personal.linkedinLink"
          @input="() => { validateUrlField('linkedinLink', personal.linkedinLink); emitUpdate(); }"
          :class="{ 'is-invalid': urlErrors.linkedinLink }"
          placeholder="https://linkedin.com/in/yourprofile"
        >
        <div v-if="urlErrors.linkedinLink" class="invalid-feedback d-block">
          {{ urlErrors.linkedinLink }}
        </div>
      </div>

      <div class="col-md-4 d-flex flex-column">
        <label class="form-label">
          <i class="bi bi-twitter me-2"></i>Twitter Link
        </label>
        <input
          type="text"
          class="form-control"
          v-model="personal.twitterLink"
          @input="() => { validateUrlField('twitterLink', personal.twitterLink); emitUpdate(); }"
          :class="{ 'is-invalid': urlErrors.twitterLink }"
          placeholder="https://twitter.com/yourhandle"
        >
        <div v-if="urlErrors.twitterLink" class="invalid-feedback d-block">
          {{ urlErrors.twitterLink }}
        </div>
      </div>

      <div class="col-md-4 d-flex flex-column">
        <label class="form-label">
          <i class="bi bi-facebook me-2"></i>Facebook Link
        </label>
        <input
          type="text"
          class="form-control"
          v-model="personal.facebookLink"
          @input="() => { validateUrlField('facebookLink', personal.facebookLink); emitUpdate(); }"
          :class="{ 'is-invalid': urlErrors.facebookLink }"
          placeholder="https://facebook.com/yourprofile"
        >
        <div v-if="urlErrors.facebookLink" class="invalid-feedback d-block">
          {{ urlErrors.facebookLink }}
        </div>
      </div>
    </div>
  </div>
  <!-- Europe PMC Search Modal (full wrapper) -->
  <div
    class="modal fade"
    id="pmcSearchModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">

        <div class="modal-header">
          <h5 class="modal-title">Europe PMC Search</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">

          <!-- Search Bar -->
          <div class="pmc-search-bar">
            <input
              type="text"
              class="form-control"
              placeholder="Search publications..."
              v-model="pmcSearchTerm"
            >
            <button class="btn btn-info" @click="searchPMC">Search</button>
          </div>

          <!-- Results -->
          <div class="pmc-results" v-if="pmcResults.length > 0">
            <div
              class="pmc-item"
              v-for="item in pmcResults"
              :key="item.id"
            >
              <h5>{{ item.title }}</h5>

              <p><strong>Authors:</strong> {{ item.authors }}</p>
              <p><strong>Journal:</strong> {{ item.journal }}</p>

              <p>
                <strong>Open Access:</strong>
                <span :class="item.openAccess ? 'text-success' : 'text-danger'">
                {{ item.openAccess ? 'Yes' : 'No' }}
              </span>
              </p>

              <button
                class="btn import-btn"
                :class="item.openAccess ? 'btn-success' : 'btn-secondary'"
                :disabled="!item.openAccess"
                @click="importPublication(item)"
              >
                Import
              </button>

              <div v-if="!item.openAccess" class="text-danger mt-2">
                🚫 This publication is not open access.
              </div>
            </div>
          </div>

          <!-- No Results -->
          <p v-if="pmcResults.length === 0 && pmcSearched">No results found.</p>

        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>

      </div>
    </div>
  </div>

  <!-- Toast Notification -->
  <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 99999;">
    <div
      class="toast fade"
      :class="{
      show: toast.show,
      'bg-success': toast.type === 'success',
      'bg-danger': toast.type === 'error'
    }"
      role="alert"
    >
      <div
        class="toast-header text-white"
        :class="{
        'bg-success': toast.type === 'success',
        'bg-danger': toast.type === 'error'
      }"
      >
        <i class="bi me-2"
           :class="toast.type === 'success'
          ? 'bi-check-circle-fill'
          : 'bi-exclamation-triangle-fill'"
        ></i>
        <strong class="me-auto">
          {{ toast.type === 'success' ? 'Success' : 'Error' }}
        </strong>
        <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
      </div>

      <div class="toast-body text-white">
        {{ toast.message }}
      </div>
    </div>
  </div>

</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import * as bootstrap from 'bootstrap'
import ForumService from '@/services/forums/ForumServices'
import WorkingGroupServices from '@/services/workinggroups/WorkingGroupServices'
import ProfileService from '@/services/profile/ProfileApiService'

export default {
  name: 'PersonalDetailsForm',
  props: {
    formData: {
      type: Object,
      default: () => ({})
    },
    profileData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update'],
  setup(props, { emit }) {
    const previousWorkingGroups = ref([])
    const acceptedWorkingGroups = ref([])
    const pendingRequestGroupIds = ref(new Set())

    const pmcSearchTerm = ref("");
    const pmcResults = ref([]);
    const pmcSearched = ref(false);

    const showOrcidInput = ref(false);

    const openPMCModal = () => {
      const el = document.getElementById("pmcSearchModal");
      if (!el) {
        console.warn("pmcSearchModal element not found");
        return;
      }
      const modal = new bootstrap.Modal(el);
      modal.show();
    };

    const toast = ref({
      show: false,
      type: 'success',
      message: ''
    })

    const showToast = (type, message) => {
      toast.value = {
        show: true,
        type,
        message
      }
      setTimeout(() => {
        toast.value.show = false
      }, 3000)
    }

    const workingGroups = ref([])

    const africanCountries = ref([
      { value: 'DZ', label: 'Algeria' },
      { value: 'AO', label: 'Angola' },
      { value: 'BJ', label: 'Benin' },
      { value: 'BW', label: 'Botswana' },
      { value: 'BF', label: 'Burkina Faso' },
      { value: 'BI', label: 'Burundi' },
      { value: 'CM', label: 'Cameroon' },
      { value: 'CV', label: 'Cape Verde' },
      { value: 'CF', label: 'Central African Republic' },
      { value: 'TD', label: 'Chad' },
      { value: 'KM', label: 'Comoros' },
      { value: 'CG', label: 'Republic of the Congo (Brazzaville)' },
      { value: 'CD', label: 'Democratic Republic of the Congo (Kinshasa)' },
      { value: 'DJ', label: 'Djibouti' },
      { value: 'EG', label: 'Egypt' },
      { value: 'GQ', label: 'Equatorial Guinea' },
      { value: 'ER', label: 'Eritrea' },
      { value: 'SZ', label: 'Eswatini (Swaziland)' },
      { value: 'ET', label: 'Ethiopia' },
      { value: 'GA', label: 'Gabon' },
      { value: 'GM', label: 'Gambia' },
      { value: 'GH', label: 'Ghana' },
      { value: 'GN', label: 'Guinea' },
      { value: 'GW', label: 'Guinea-Bissau' },
      { value: 'CI', label: 'Ivory Coast (Côte d’Ivoire)' },
      { value: 'KE', label: 'Kenya' },
      { value: 'LS', label: 'Lesotho' },
      { value: 'LR', label: 'Liberia' },
      { value: 'LY', label: 'Libya' },
      { value: 'MG', label: 'Madagascar' },
      { value: 'MW', label: 'Malawi' },
      { value: 'ML', label: 'Mali' },
      { value: 'MR', label: 'Mauritania' },
      { value: 'MU', label: 'Mauritius' },
      { value: 'MA', label: 'Morocco' },
      { value: 'MZ', label: 'Mozambique' },
      { value: 'NA', label: 'Namibia' },
      { value: 'NE', label: 'Niger' },
      { value: 'NG', label: 'Nigeria' },
      { value: 'RW', label: 'Rwanda' },
      { value: 'ST', label: 'São Tomé and Príncipe' },
      { value: 'SN', label: 'Senegal' },
      { value: 'SC', label: 'Seychelles' },
      { value: 'SL', label: 'Sierra Leone' },
      { value: 'SO', label: 'Somalia' },
      { value: 'ZA', label: 'South Africa' },
      { value: 'SS', label: 'South Sudan' },
      { value: 'SD', label: 'Sudan' },
      { value: 'TZ', label: 'Tanzania' },
      { value: 'TG', label: 'Togo' },
      { value: 'TN', label: 'Tunisia' },
      { value: 'UG', label: 'Uganda' },
      { value: 'ZM', label: 'Zambia' },
      { value: 'ZW', label: 'Zimbabwe' }
    ]);

    const personal = ref({
      title: '',
      country: '',
      currentOrganisation: '',
      currentJobTitle: '',
      selectedWorkingGroup1: '',
      selectedWorkingGroup2: '',
      biography: '',
      linkedinLink: '',
      twitterLink: '',
      facebookLink: '',
      email: '',
      orcidId: '',
      // Add these to track the actual working groups for API
      workingGroups: []
    })

    const urlErrors = ref({
      linkedinLink: '',
      twitterLink: '',
      facebookLink: ''
    })

// very permissive URL checker (allows domain-only; backend will still hard-check)
    const urlRegex = /^(https?:\/\/)?([\w.-]+)\.[a-z]{2,}(:\d+)?(\/[\w\-._~:/?#[\]@!$&'()*+,;=%.]*)?$/i

    const normalizeUrl = (url) => {
      if (!url) return ''
      return /^https?:\/\//i.test(url) ? url : `https://${url}`
    }

    const validateUrlField = (field, value) => {
      if (!value) {
        urlErrors.value[field] = ''
        return true
      }
      const ok = urlRegex.test(value)
      urlErrors.value[field] = ok
        ? ''
        : 'Please enter a valid link (e.g. https://linkedin.com/in/yourname)'
      return ok
    }

    // Helper function to convert working groups array to selected values
    const setWorkingGroupsFromArray = (workingGroupsArray) => {
      if (!Array.isArray(workingGroupsArray)) {
        return
      }

      // Clear current selections
      personal.value.selectedWorkingGroup1 = ''
      personal.value.selectedWorkingGroup2 = ''

      // Set up to 2 working groups
      if (workingGroupsArray.length > 0) {
        personal.value.selectedWorkingGroup1 = workingGroupsArray[0]
      }
      if (workingGroupsArray.length > 1) {
        personal.value.selectedWorkingGroup2 = workingGroupsArray[1]
      }

      // Store the full array
      personal.value.workingGroups = [...workingGroupsArray]
    }

    // Helper function to convert selected values back to array
    const getWorkingGroupsArray = () => {
      const groups = []
      if (personal.value.selectedWorkingGroup1) {
        groups.push(personal.value.selectedWorkingGroup1)
      }
      if (personal.value.selectedWorkingGroup2 &&
        personal.value.selectedWorkingGroup2 !== personal.value.selectedWorkingGroup1) {
        groups.push(personal.value.selectedWorkingGroup2)
      }
      return groups
    }

    const searchPMC = async () => {
      if (!pmcSearchTerm.value) return;

      pmcSearched.value = true;

      try {
        const response = await fetch(
          `https://www.ebi.ac.uk/europepmc/webservices/rest/search?query=${encodeURIComponent(
            pmcSearchTerm.value
          )}&format=json`
        );

        const data = await response.json();
        const results = data.resultList?.result || [];

        pmcResults.value = results.map(item => ({
          id: item.id,
          title: item.title,
          authors: item.authorString,
          journal: item.journalTitle,
          openAccess: item.isOpenAccess === "Y",
          raw: item
        }));
      } catch (e) {
        console.error("PMC Search Error:", e);
      }
    }

    const loadingImport = ref(false)

    const importPublication = async (item) => {
      if (!item.openAccess) {
        showToast('error', 'This publication is not open access and cannot be imported.')
        return
      }

      const payload = {
        title: item.title || '',
        authors: item.authorString || item.authors || '',
        date: item.pubYear ? `${item.pubYear}-01-01` : (item.pubDate || null),
        journal: item.journalTitle || item.journal || '',
        volume: item.journalVolume || '',
        issue: item.journalIssue || '',
        // send snake_case the backend expects
        open_access: (item.isOpenAccess === 'Y') || item.openAccess === true || !!item.openAccess,
        // important: provide a pm_id field if present
        pm_id: item.id || item.pmid || item.pmcid || null,
        external_identifiers: JSON.stringify({
          source: 'europepmc',
          id: item.id
        }),
        fos: item.fos || '',
        orcid_work: item.orcid_work || '',
        citation: item.citation || '',
        content: item.abstractText || item.content || '',
        raw: item.raw || item
      }

      try {
        const res = await ProfileService.importPublication(payload)

        if (res?.success || res?.Success) {
          showToast('success', `Imported: ${payload.title}`)

          // 🔥 force profile reload so publications refresh
          emit('update', { reloadProfile: true })
        } else {
          showToast('error', res?.message || 'Failed to import publication')
        }
      } catch (e) {
        showToast('error', e.message || 'Error importing publication')
      }
    }

    // Parse working groups from different possible formats
    const parseWorkingGroups = (data) => {

      // Try multiple possible sources for working groups
      let workingGroupsData =
        data.workingGroups ||
        data.working_groups ||
        data.working_group ||
        data.personal?.workingGroups ||
        data.fellow_details?.working_group ||
        data.user?.fellow_details?.working_group ||
        []


      // Handle string format (comma-separated)
      if (typeof workingGroupsData === 'string') {
        // Handle comma-separated values
        if (workingGroupsData.includes(',')) {
          return workingGroupsData.split(',').map(wg => wg.trim()).filter(wg => wg)
        }

        // Handle JSON string
        if (workingGroupsData.startsWith('[') || workingGroupsData.startsWith('{')) {
          try {
            const result = JSON.parse(workingGroupsData)
            return Array.isArray(result) ? result : []
          } catch (e) {
            console.warn('PersonalDetailsForm - Failed to parse working groups JSON:', e)
            return []
          }
        }

        // Single string value
        return workingGroupsData ? [workingGroupsData] : []
      }

      // Handle array
      if (Array.isArray(workingGroupsData)) {
        return workingGroupsData
      }

      return []
    }

    // Load working groups from API
    const loadWorkingGroups = async () => {
      try {
        const response = await ForumService.getWorkingGroups();

        if (response?.data?.data && Array.isArray(response.data.data)) {
          // Handle nested data correctly
          workingGroups.value = response.data.data.filter(group => group && group.id && group.name)
        } else if (response?.data && Array.isArray(response.data)) {
          // Fallback for simpler API responses
          workingGroups.value = response.data.filter(group => group && group.id && group.name)
        } else {
          console.warn('⚠️ Unexpected working group response structure:', response)
          workingGroups.value = []
        }
      } catch (error) {
        console.error('PersonalDetailsForm - Error loading working groups:', error);
        workingGroups.value = [];
      }
    }

    // Updated getWorkingGroupName to handle both ID and name formats
    const getWorkingGroupName = (groupId) => {
      if (!groupId) return ''

      // First try to find by ID (exact match)
      const groupById = workingGroups.value.find(g => g.id === groupId)
      if (groupById) {
        return groupById.name
      }

      // Try string comparison (in case of type mismatch)
      const groupByStringId = workingGroups.value.find(g => String(g.id) === String(groupId))
      if (groupByStringId) {
        return groupByStringId.name
      }

      // Then try to find by name (in case the value is already a name)
      const groupByName = workingGroups.value.find(g => g.name === groupId)
      if (groupByName) {
        return groupByName.name
      }

      return groupId
    }

    const isPendingApproval = (groupId) => {
      return pendingRequestGroupIds.value.has(String(groupId))
    }

    const getWorkingGroupOptionLabel = (group) => {
      if (!group?.id || !group?.name) return ''
      return isPendingApproval(group.id) ? `${group.name} (Pending request)` : group.name
    }

    const loadMyApprovalStatus = async () => {
      try {
        const response = await WorkingGroupServices.getMyApprovalStatus()
        const approvals = response?.data?.data

        if (!Array.isArray(approvals)) {
          pendingRequestGroupIds.value = new Set()
          return
        }

        const pending = approvals
          .filter((approval) => approval?.status === 'pending' && approval?.working_group_id)
          .map((approval) => String(approval.working_group_id))

        pendingRequestGroupIds.value = new Set(pending)
      } catch (error) {
        console.error('PersonalDetailsForm - Error loading approval status:', error)
        pendingRequestGroupIds.value = new Set()
      }
    }

    // Computed property to get the actual data from props
    const actualData = computed(() => {
      let data = null

      if (props.profileData?.data) {
        data = props.profileData.data
      } else if (props.profileData && Object.keys(props.profileData).length > 0) {
        data = props.profileData
      } else if (props.formData && Object.keys(props.formData).length > 0) {
        data = props.formData
      }

      return data
    })

    const normalizeTitle = (value) => {
      if (!value) return '';

      const v = String(value).trim().toLowerCase();

      const map = {
        "mr": "Mr",
        "mister": "Mr",
        "mrs": "Mrs",
        "missus": "Mrs",
        "ms": "Ms",
        "dr": "Dr",
        "doctor": "Dr",
        "prof": "Professor",
        "prof.": "Professor",
        "professor": "Professor",
        "associate professor": "Associate Professor"
      };

      return map[v] || value;
    };

    // Initialize data from props
    const initializeData = async () => {

      // Load working groups first
      await loadWorkingGroups()
      await loadMyApprovalStatus()

      const data = actualData.value

      if (!data) {
        return
      }

      // Extract data from different possible structures
      const userData = data.user || {}
      const fellowData = data.user?.fellow_details || data.fellow_details || {}
      const personalData = data.personal || {}
      const formPersonal = data.formData?.personal || {}

      // Title
      personal.value.title = normalizeTitle(
        personalData.title ||
        userData.title ||
        fellowData.title ||
        ''
      );

      // Country
      personal.value.country =
        personalData.country ||
        userData.country ||
        fellowData.country ||
        '';

      // Current Organisation
      personal.value.currentOrganisation =
        personalData.currentOrganisation ||
        userData.currentOrganisation ||
        fellowData.currentOrganisation ||
        '';

      // Current Job Title
      personal.value.currentJobTitle =
        personalData.currentJobTitle ||
        userData.currentJobTitle ||
        fellowData.currentJobTitle ||
        '';

      // Biography
      personal.value.biography =
        personalData.biography ||
        fellowData.biography ||
        '';

      // ORCID
      personal.value.orcidId =
        personalData.orcidId ||
        fellowData.orcidId ||
        '';

      // Social links
      personal.value.linkedinLink =
        personalData.linkedinLink ||
        fellowData.linkedin_link ||
        '';

      personal.value.twitterLink =
        personalData.twitterLink ||
        fellowData.twitter_link ||
        '';

      personal.value.facebookLink =
        personalData.facebookLink ||
        fellowData.facebook_link ||
        '';

      // Handle working groups with improved logic
      const workingGroupsArray = parseWorkingGroups(data)

      setWorkingGroupsFromArray(workingGroupsArray)
      acceptedWorkingGroups.value = [...getWorkingGroupsArray()]
      previousWorkingGroups.value = [...getWorkingGroupsArray()]

      // Emit initial data with a slight delay to ensure everything is set
      setTimeout(() => {
        emitUpdate()
      }, 100)
    }

    // Updated emit function to include working groups array
    const emitUpdate = () => {
      personal.value.workingGroups = getWorkingGroupsArray()

      ;['linkedinLink', 'twitterLink', 'facebookLink'].forEach(k => {
        personal.value[k] = normalizeUrl(personal.value[k])
        validateUrlField(k, personal.value[k])
      })

      const data = {
        personal: {
          ...personal.value,
          workingGroups: personal.value.workingGroups
        }
      }

      emit('update', data)
    }

    // Handle Working Group Selection and trigger join request
    const handleWorkingGroupSelection = (groupId, slot) => {
      if (!groupId) return

      if (isPendingApproval(groupId)) {
        showToast('error', 'A request for this working group is already pending approval.')

        if (slot === 1) {
          personal.value.selectedWorkingGroup1 = ''
        } else {
          personal.value.selectedWorkingGroup2 = ''
        }

        emitUpdate()
        return
      }

      // Prevent duplicate selection
      if (
        slot === 2 &&
        groupId === personal.value.selectedWorkingGroup1
      ) {
        showToast('error', 'You cannot select the same working group twice.')
        personal.value.selectedWorkingGroup2 = ''
        return
      }

      // Enforce max 2 locally
      const selected = getWorkingGroupsArray()
      if (selected.length > 2) {
        showToast('error', 'You may only select two working groups.')
        return
      }

      // 🔥 SHOW TOAST IF THIS IS A NEW REQUEST
      if (!acceptedWorkingGroups.value.includes(groupId)) {
        showToast(
          'success',
          `Request to join ${getWorkingGroupName(groupId)} sent. Awaiting approval.`
        )
      }

      emitUpdate()
    }


    // Updated remove methods
    const removeWorkingGroup1 = () => {
      personal.value.selectedWorkingGroup1 = ''
      personal.value.workingGroups = getWorkingGroupsArray()
      emitUpdate()
    }

    const removeWorkingGroup2 = () => {
      personal.value.selectedWorkingGroup2 = ''
      personal.value.workingGroups = getWorkingGroupsArray()
      emitUpdate()
    }

    // Watch for changes in the computed actualData
    watch(actualData, (newData) => {
      if (newData && Object.keys(newData).length > 0) {
        initializeData()
      }
    }, { deep: true, immediate: true })

    // Watch for changes in formData.personal specifically
    watch(
      () => props.profileData,
      (newVal, oldVal) => {
        if (!newVal || !oldVal) return

        const before = previousWorkingGroups.value
        const after = parseWorkingGroups(newVal)

        // Newly requested groups
        const newlyRequested = after.filter(id => !before.includes(id))

        if (newlyRequested.length > 0) {
          const names = newlyRequested
            .map(id => getWorkingGroupName(id))
            .join(', ')

          showToast(
            'success',
            `Request to join ${names} sent. Awaiting approval.`
          )
        }

        // Update snapshot
        previousWorkingGroups.value = [...after]
      },
      { deep: true }
    )


    // Initialize on mount
    onMounted(() => {
      setTimeout(() => {
        initializeData()
      }, 100)
    })

    return {
      personal,
      workingGroups,
      africanCountries,
      removeWorkingGroup1,
      removeWorkingGroup2,
      getWorkingGroupName,
      emitUpdate,
      initializeData,
      handleWorkingGroupSelection,
      isPendingApproval,
      getWorkingGroupOptionLabel,
      urlErrors,
      validateUrlField,
      showOrcidInput,

      openPMCModal,
      pmcSearchTerm,
      pmcResults,
      pmcSearched,
      searchPMC,
      importPublication,

      toast,
      showToast
    }
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.specialisation-tags,
.working-group-tags {
  min-height: 30px;
}

.badge {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

.badge i {
  margin-left: 0.25rem;
}

.badge i:hover {
  opacity: 0.7;
}

h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.text-muted {
  font-size: 0.875rem;
  color: #6c757d !important;
}

.section-header {
  margin: 2rem 0 0.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Make modal appear above the form cleanly */
#pmcSearchModal .modal-dialog {
  max-width: 900px !important;
}

#pmcSearchModal .modal-content {
  border-radius: 12px;
  padding: 10px 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
}

/* Header styling */
#pmcSearchModal .modal-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
}

/* Search bar styling */
#pmcSearchModal .pmc-search-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

/* Search results container */
#pmcSearchModal .pmc-results {
  max-height: 450px;
  overflow-y: auto;
  padding-right: 5px;
}

/* Individual result card */
#pmcSearchModal .pmc-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

#pmcSearchModal .pmc-item h5 {
  font-size: 1rem;
  color: #0b7285;
  margin-bottom: 8px;
}

#pmcSearchModal .pmc-item p {
  margin: 2px 0;
  font-size: 0.9rem;
}

/* Import button alignment */
#pmcSearchModal .import-btn {
  float: right;
  margin-top: 10px;
}

/* Close button spacing */
#pmcSearchModal .modal-footer {
  border-top: 1px solid #e5e7eb;
  padding-top: 15px;
}

.pmc-item .import-btn {
  align-self: flex-end;
  margin-top: 12px;
}

</style>
